import { Flow } from 'signup-constants/Flow';
import { isntExistingUserExistingAccount } from '../app/verificationTypeSelectors';
import { areTherePostAccountCreationSteps, onlyMobileAppPromptStep } from '../flow-management/stepNumberingSelectors';
import { EndpointKey } from '../http/EndpointKey';
import { getHasRefreshedNewPortalAuth } from '../store/app/selectors';
import { arePortalExperimentsFetched } from '../store/experiments/selectors';
import { hasPostExistingAccountSelectionQuestions, isHSFXFLow } from '../store/flow/selectors';
import { isEndpointStatusSuccess } from '../store/http-responses/selectors';
import { isCopyCarouselFinished } from '../store/loading-status/selectors';
import { getIsMobile } from '../utils/deviceUtils';
import { isFlow } from '../utils/isFlow';
import BaseLoadingView from './BaseLoadingView';
import IllustrationLoadingView from './IllustrationLoadingView';
import { LoadingKey } from './LoadingKey';
import { getSignupRequestLoadingImageConfig } from './loadingUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const baseLoadingView = ({
  loadingKey,
  hasSpinner,
  imageUrl,
  imageClass,
  textDisplayDelay,
  isIllustration,
  hasConfetti,
  illustrationHeight,
  hasLoadingDots,
  setTimeoutForCopyCarousel
}) => () => /*#__PURE__*/_jsx(BaseLoadingView, {
  hasConfetti: hasConfetti,
  hasLoadingDots: hasLoadingDots,
  hasSpinner: hasSpinner,
  illustrationHeight: illustrationHeight,
  imageClass: imageClass,
  imageUrl: imageUrl,
  isIllustration: isIllustration,
  loadingKey: loadingKey,
  setTimeoutForCopyCarousel: setTimeoutForCopyCarousel,
  textDisplayDelay: textDisplayDelay
});
const illustrationLoadingView = ({
  loadingKey,
  hasSpinner = false,
  illustration,
  imageClass = '',
  textDisplayDelay = 0,
  hasConfetti
}) => () => /*#__PURE__*/_jsx(IllustrationLoadingView, {
  hasConfetti: hasConfetti,
  hasSpinner: hasSpinner,
  illustration: illustration,
  imageClass: imageClass,
  loadingKey: loadingKey,
  textDisplayDelay: textDisplayDelay
});
const simpleLoadingScreen = ({
  loadingKey,
  imageUrl = '',
  hasSpinner = true,
  shouldClearLoading,
  imageClass = '',
  canSwitchCopy = () => true,
  shouldNotOverrideOtherLoading = () => false,
  textDisplayDelay = 0,
  shouldShowProgressBar = () => false,
  isIllustration = false,
  hasConfetti = false,
  illustrationHeight,
  hasLoadingDots = false,
  setTimeoutForCopyCarousel = 0
}) => ({
  [loadingKey]: {
    view: baseLoadingView({
      loadingKey,
      imageUrl,
      hasSpinner,
      imageClass,
      textDisplayDelay,
      isIllustration,
      hasConfetti,
      illustrationHeight,
      hasLoadingDots,
      setTimeoutForCopyCarousel
    }),
    shouldClearLoading,
    canSwitchCopy,
    shouldNotOverrideOtherLoading,
    shouldShowProgressBar
  }
});
const illustrationLoading = ({
  loadingKey,
  hasSpinner,
  shouldClearLoading,
  imageClass,
  shouldNotOverrideOtherLoading = () => false,
  textDisplayDelay = 0,
  illustration,
  shouldShowProgressBar = () => false,
  hasConfetti = false
}) => ({
  [loadingKey]: {
    view: illustrationLoadingView({
      loadingKey,
      illustration,
      hasSpinner,
      imageClass,
      textDisplayDelay,
      hasConfetti
    }),
    shouldClearLoading,
    shouldNotOverrideOtherLoading,
    shouldShowProgressBar
  }
});
const makeSimpleLoadingScreens = () => {
  return Object.keys(LoadingKey).reduce((obj, loadingKey) => Object.assign({}, obj, simpleLoadingScreen({
    loadingKey: loadingKey,
    hasLoadingDots: true
  })), {});
};
export const LoadingScreen = Object.assign({}, makeSimpleLoadingScreens(), simpleLoadingScreen({
  loadingKey: LoadingKey.LoginAfterSignup,
  hasSpinner: true,
  hasLoadingDots: true,
  textDisplayDelay: 1500,
  shouldNotOverrideOtherLoading: state => !areTherePostAccountCreationSteps(state),
  shouldClearLoading: () => getIsMobile(),
  canSwitchCopy: state => {
    return getHasRefreshedNewPortalAuth(state);
  }
}), illustrationLoading({
  loadingKey: LoadingKey.PartnerExistingAccount,
  illustration: 'bolt',
  shouldClearLoading: state => {
    return isFlow(Flow.DirectoryListing) && isEndpointStatusSuccess(EndpointKey.PartnerLookup)(state);
  }
}), simpleLoadingScreen(Object.assign({
  loadingKey: LoadingKey.SignupRequest
}, getSignupRequestLoadingImageConfig(), {
  hasSpinner: false,
  hasConfetti: true,
  shouldClearLoading: state => {
    if (!areTherePostAccountCreationSteps(state)) {
      return false;
    }
    const copyCarouselCheck = !onlyMobileAppPromptStep(state) ? isCopyCarouselFinished(state, LoadingKey.SignupRequest) : true;
    return isEndpointStatusSuccess(EndpointKey.Signup)(state) && copyCarouselCheck && (arePortalExperimentsFetched(state) || onlyMobileAppPromptStep(state));
  },
  canSwitchCopy: state => {
    return isEndpointStatusSuccess(EndpointKey.Signup)(state) && areTherePostAccountCreationSteps(state) && !onlyMobileAppPromptStep(state);
  },
  shouldShowProgressBar: isHSFXFLow
})), simpleLoadingScreen({
  loadingKey: LoadingKey.PostSignupLoading,
  hasSpinner: true,
  hasLoadingDots: true,
  shouldNotOverrideOtherLoading: state => !hasPostExistingAccountSelectionQuestions(state) || isntExistingUserExistingAccount(state),
  shouldClearLoading: state => !hasPostExistingAccountSelectionQuestions(state),
  shouldShowProgressBar: isHSFXFLow
}), simpleLoadingScreen({
  loadingKey: LoadingKey.InitiateVerificationRequestExpiredRefresh,
  hasSpinner: false,
  hasLoadingDots: true,
  setTimeoutForCopyCarousel: 1500,
  shouldClearLoading: state => {
    return isCopyCarouselFinished(state, LoadingKey.InitiateVerificationRequestExpiredRefresh) && isEndpointStatusSuccess(EndpointKey.InitiateVerificationExpiredRefresh)(state);
  },
  shouldShowProgressBar: () => true
}));
export const getLoading = loadingKey => {
  return LoadingScreen[loadingKey];
};